/**
 * Reusable Table Component
 * - Displays data in tabular format
 * - Supports pagination, sorting, and custom rendering
 * - Responsive design for mobile/desktop
 * - Clickable rows with custom actions
 */

import React from "react";
import { FiEye, FiTrash2 } from "react-icons/fi";
import Pagination from "./Pagination";

export default function TableComponent({
  data,
  columns,
  onRowClick,
  onView,
  onDelete,
  // Pagination props
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  showPagination = true,
}) {

  return (
    <div className="mt-6 overflow-x-auto px-3 sm:px-4 md:px-6  pb-10">
      {/* ✅ Desktop / Large Tablet Table */}
      <div className="hidden lg:block">
        <div className="border border-gray-200 rounded-lg overflow-hidden shadow-md">
          <table className="w-full text-left border-collapse">
         <thead className="relative z-10">
  <tr className="bg-gray-50 shadow-[0_6px_10px_-2px_rgba(0,0,0,0.10)]
">
    {columns.map((col) => (
      <th
        key={col.key}
        className="py-4 px-4 font-bold text-sm whitespace-nowrap border-b border-gray-300 text-gray-700"
      >
        {col.label}
      </th>
    ))}
  </tr>
</thead>


<tbody className="text-gray-800 text-sm">
  {data?.length ? (
    data.map((row, index) => (
      <tr
        key={row.id || index}
        className={`cursor-pointer transition-colors ${
          index % 2 === 0 ? " bg-gray-50" : "bg-white"
        } hover:bg-blue-50 border-b border-gray-200 last:border-b-0`}
        onClick={() => onRowClick && onRowClick(row)}
      >
        {columns.map((col) => (
          <td
            key={col.key}
            className={`py-4 px-4 align-top last:border-r-0 ${
              col.tdClass || "text-gray-900"
            }`}
          >
            {col.key === "action" ? (
              <div className="flex gap-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onView && onView(row);
                  }}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  title="View"
                >
                  <FiEye size={18} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete && onDelete(row);
                  }}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  title="Delete"
                >
                  <FiTrash2 size={18} />
                </button>
              </div>
            ) : col.render ? (
              col.render(row[col.key], row, index)
            ) : col.key === "pets" && Array.isArray(row[col.key]) ? (
              row[col.key].length
            ) : typeof row[col.key] === "object" && row[col.key] !== null ? (
              Array.isArray(row[col.key])
                ? row[col.key]
                    .map((i) =>
                      typeof i === "object" && i !== null
                        ? Object.values(i).join(" / ")
                        : i
                    )
                    .join(", ")
                : Object.values(row[col.key]).join(" / ")
            ) : (
              row[col.key] || "N/A"
            )}
          </td>
        ))}
      </tr>
    ))
  ) : (
    <tr>
      <td
        colSpan={columns.length}
        className="text-center text-gray-400 py-12 text-base border-t border-gray-200"
      >
        No records found.
      </td>
    </tr>
  )}
</tbody>

          </table>
        </div>
      </div>

      {/* ✅ Tablet & Mobile Card Layout */}
      <div className="block lg:hidden space-y-5 mb-10">
        {data?.length ? (
          data.map((row, index) => (
            <div
              key={row.id || index}
              className="bg-white border border-gray-200 rounded-xl p-5 shadow-lg hover:shadow-md transition "
              onClick={() => onRowClick && onRowClick(row)}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {columns.map((col) =>
                  col.key !== "action" ? (
                    <div
                      key={col.key}
                      className="flex justify-between sm:justify-start sm:flex-col"
                    >
                      <span className="text-gray-800 text-sm sm:text-base font-semibold">
                        {col.label}
                      </span>
                      <span className="text-gray-700 text-sm sm:text-base text-right sm:text-left">
                        {col.render
                          ? col.render(row[col.key], row, index)
                          : typeof row[col.key] === "object" &&
                            row[col.key] !== null
                          ? Array.isArray(row[col.key])
                            ? row[col.key]
                                .map((i) =>
                                  typeof i === "object" && i !== null
                                    ? Object.values(i).join(" / ")
                                    : i
                                )
                                .join(", ")
                            : Object.values(row[col.key]).join(" / ")
                          : row[col.key] || "N/A"}
                      </span>
                    </div>
                  ) : null
                )}
              </div>

              {/* Action buttons */}
             
            </div>
          ))
        ) : (
          <div className="text-center text-gray-400 py-8 text-sm sm:text-base">
            No records found.
          </div>
        )}
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
